import { useMutation, type DefaultError, type UseMutationResult } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';

import {
    RecurringSubscriptionBillingAmountCreationForm,
    RecurringSubscriptionBillingAmountDeletionForm,
    RecurringSubscriptionCreationForm,
    RecurringSubscriptionParticipantsUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import { RecurringSubscriptionStatus } from 'chaika-enums';

import { RecurringSubscriptionApi } from '@api/RecurringSubscriptionApi';
import { FinanceRefundApi } from '@api/FinanceRefundApi';
import { queryClient } from '@store/queryClient';

import { recurringSubscriptionKeys, recurringSubscriptionQueryOptions } from './recurringSubscriptionQueries';

export function useCreateRecurringSubscription(): UseMutationResult<
    void,
    DefaultError,
    RecurringSubscriptionCreationForm
> {
    return useMutation({
        mutationFn: async (params: RecurringSubscriptionCreationForm) => {
            await RecurringSubscriptionApi.create({ ...params, withTrial: true });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recurringSubscriptionKeys.all });
        },
    });
}

export function useRefundRecurringSubscription(): UseMutationResult<void, DefaultError, string> {
    return useMutation({
        mutationFn: async (id: string) => {
            const recurringSubscription = await queryClient.ensureQueryData(recurringSubscriptionQueryOptions.byId(id));

            if (
                !recurringSubscription.isTrial &&
                !isEmpty(recurringSubscription.paymentIds) &&
                recurringSubscription.status === RecurringSubscriptionStatus.Active
            ) {
                const [paymentId] = recurringSubscription.paymentIds;
                await FinanceRefundApi.refundByPaymentid(paymentId);
            }
        },
    });
}

interface UpdateParticipantsVariables {
    id: string;
    params: RecurringSubscriptionParticipantsUpdateForm;
}

export function useUpdateRecurringSubscriptionParticipants(): UseMutationResult<
    void,
    DefaultError,
    UpdateParticipantsVariables
> {
    return useMutation({
        mutationFn: ({ id, params }: UpdateParticipantsVariables) =>
            RecurringSubscriptionApi.updateParticipants(id, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recurringSubscriptionKeys.all });
        },
    });
}

export function useUpdateRecurringSubscriptionBillingAmount(): UseMutationResult<
    void,
    DefaultError,
    RecurringSubscriptionBillingAmountCreationForm
> {
    return useMutation({
        mutationFn: (params: RecurringSubscriptionBillingAmountCreationForm) =>
            RecurringSubscriptionApi.updateBillingAmount(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recurringSubscriptionKeys.all });
        },
    });
}

export function useDeleteRecurringSubscriptionBillingAmount(): UseMutationResult<
    void,
    DefaultError,
    RecurringSubscriptionBillingAmountDeletionForm
> {
    return useMutation({
        mutationFn: (params: RecurringSubscriptionBillingAmountDeletionForm) =>
            RecurringSubscriptionApi.deleteBillingAmount(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recurringSubscriptionKeys.all });
        },
    });
}
